import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';

import { BaseButton, useIsMobile } from '@repo/common';
import PreviewDownloadModal from './PreviewDownloadModal';
import PreviewPagedContent from './PreviewPagedContent';

import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/16px/icon_16px_download.svg';
import { ReactComponent as ZoomInIcon } from '@repo/common/assets/icons/16px/icon_16px_zoom-in.svg';
import { ReactComponent as ZoomOutIcon } from '@repo/common/assets/icons/16px/icon_16px_zoom-out.svg';
import { ReactComponent as FullScreenIcon } from '@repo/common/assets/icons/16px/icon_16px_fullscreen.svg';

const PreviewFullPage = ({ resumeData, onDownload, isApply }) => {
  const isMobile = useIsMobile();
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const zoomLevels = [50, 75, 100, 125, 150, 200];
  const [zoomIndex, setZoomIndex] = useState(2);
  const [currentZoom, setCurrentZoom] = useState(100);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [enableTransition, setEnableTransition] = useState(false);

  const containerRef = useRef(null);

  const handleZoomIn = () => {
    setIsFullScreen(false);
    const nextIndex = Math.min(zoomIndex + 1, zoomLevels.length - 1);
    setZoomIndex(nextIndex);
    setCurrentZoom(zoomLevels[nextIndex]);
  };

  const handleZoomOut = () => {
    if (isFullScreen) {
      const lastIndex = zoomLevels.length - 1;
      setZoomIndex(lastIndex);
      setCurrentZoom(zoomLevels[lastIndex]);
      setIsFullScreen(false);
    } else {
      const nextIndex = Math.max(zoomIndex - 1, 0);
      setZoomIndex(nextIndex);
      setCurrentZoom(zoomLevels[nextIndex]);
    }
  };

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const padding = 32;
    const availableWidth = containerRef.current.clientWidth - padding;
    const fitZoom = Math.floor((availableWidth / 595) * 100);

    setCurrentZoom(fitZoom);

    const closestIndex = zoomLevels.reduce(
      (prev, curr, idx) =>
        Math.abs(curr - fitZoom) < Math.abs(zoomLevels[prev] - fitZoom)
          ? idx
          : prev,
      0,
    );
    setZoomIndex(closestIndex);

    requestAnimationFrame(() => setEnableTransition(true));
  }, [isMobile]);

  const handleFullScreenToggle = () => {
    if (!containerRef.current) return;

    if (!isFullScreen) {
      const padding = 64;
      const availableWidth = containerRef.current.clientWidth - padding;
      const fitZoom = Math.floor((availableWidth / 595) * 100);

      setCurrentZoom(fitZoom);
      setIsFullScreen(true);

      const closestIndex = zoomLevels.reduce(
        (prev, curr, idx) =>
          Math.abs(curr - fitZoom) < Math.abs(zoomLevels[prev] - fitZoom)
            ? idx
            : prev,
        0,
      );
      setZoomIndex(closestIndex);
    } else {
      const lastIndex = zoomLevels.length - 1;
      setZoomIndex(lastIndex);
      setCurrentZoom(zoomLevels[lastIndex]);
      setIsFullScreen(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className='relative flex flex-col w-full h-[calc(100vh-118px)] bg-grayscale-gray01 overflow-hidden'
    >
      <div className='flex-1 overflow-auto flex items-start p-4 pb-[80px] min-h-0'>
        <div
          className='mx-auto'
          style={{
            width: `${(595 * currentZoom) / 100}px`,
            height: `${(842 * currentZoom) / 100}px`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              transform: `scale(${currentZoom / 100})`,
              transformOrigin: 'top left',
              transition: enableTransition ? 'transform 0.2s ease-out' : 'none',
              width: '595px',
              height: '842px',
            }}
          >
            <PreviewPagedContent resumeData={resumeData} />
          </div>
        </div>
      </div>

      <div className='flex items-center justify-center gap-2 p-6 pt-3'>
        {!isApply && (
          <BaseButton
            type='text'
            size='s'
            icon={<DownloadIcon />}
            bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
            width='w-[71px]'
            onClick={() => setIsDownloadOpen(true)}
          >
            PDF
          </BaseButton>
        )}

        <div className='flex items-center gap-3'>
          <BaseButton
            type='text'
            size='s'
            icon={<ZoomOutIcon />}
            bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
            width='w-[71px]'
            onClick={handleZoomOut}
          />
          <span className='typo-b14Strong min-w-[45px] text-center'>
            {isFullScreen ? 'Full' : `${currentZoom}%`}
          </span>
          <BaseButton
            type='text'
            size='s'
            icon={<ZoomInIcon />}
            bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
            width='w-[71px]'
            onClick={handleZoomIn}
          />
        </div>

        <BaseButton
          type='text'
          size='s'
          icon={<FullScreenIcon />}
          bgclass='bg-grayscale-gray00 border-grayscale-gray02 h-[32px]'
          width='w-[71px]'
          onClick={handleFullScreenToggle}
        />
      </div>

      {isDownloadOpen && (
        <PreviewDownloadModal
          onClose={() => setIsDownloadOpen(false)}
          onDownload={onDownload}
          hideDim
        />
      )}
    </div>
  );
};

export default PreviewFullPage;
